import { RenewalAlert, RenewalSummary } from '@policyvision/contracts';
import { Scale, StickyNoteIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/common/components/ui/badge';
import { Button } from '@/common/components/ui/button';
import { TableCell, TableRow } from '@/common/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/common/components/ui/tooltip';
import { VariationBadge } from '@/common/components/ui/variation-badge';
import { useFormatCurrency } from '@/common/hooks/useFormatCurrency';
import { formatDate } from '@/common/utils/DateFormatters';

import { COLORS } from '../utils/badges';
interface RenewalRowProps {
  renewal: RenewalSummary;
  onRowClick: (type: 'comparison' | 'details') => void;
}
export const RenewalRow = ({ renewal, onRowClick }: RenewalRowProps) => {
  const { i18n, t } = useTranslation();
  const { formatCurrency } = useFormatCurrency();

  const renderAmount = (value: number | null) => {
    if (value === null) return '-';
    const isPositive = value >= 0;
    const sign = isPositive ? '+' : '';
    return (
      <Badge
        variant={isPositive ? 'success' : 'error'}
        className="px-1.5 py-0.5"
      >
        {sign} {formatCurrency(value.toString())}
      </Badge>
    );
  };

  const getDaysLeft = (expirationDate: Date) => {
    const currentDate = new Date();
    const diffInMs = expirationDate.getTime() - currentDate.getTime();
    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
    return diffInDays;
  };

  return (
    <TableRow
      className="cursor-pointer hover:bg-warm-gray-100"
      onClick={() => onRowClick('details')}
    >
      {/* Priorisation */}
      <TableCell className="whitespace-nowrap border-r flex justify-between items-center min-w-33.5 pb-3">
        <Badge className="bg-white text-black border border-grey text-xxs px-1.5 py-0.5">
          {getDaysLeft(renewal.expirationDate)}
          {t('renewal.daysAbbr')}
        </Badge>
        {formatDate(renewal.expirationDate, i18n.language)}
      </TableCell>
      <TableCell className="whitespace-nowrap border-r min-w-42">
        {renewal.alerts && renewal.alerts.length > 0
          ? renewal.alerts?.map((alert: RenewalAlert, idx: number) => {
              return (
                <Badge
                  className="mr-2 text-xxs! px-1.5 py-0.5"
                  variant={COLORS.ALERTS[alert]}
                  key={`${alert}-${idx}`}
                >
                  {alert}
                </Badge>
              );
            })
          : '-'}
      </TableCell>
      <TableCell className="whitespace-nowrap min-w-19.25 border-r">
        {renewal.notesCount > 0 ? (
          <p className="flex items-center gap-2">
            <StickyNoteIcon className="w-4 h-4 text-muted-foreground" />
            {renewal.notesCount}
          </p>
        ) : (
          ''
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap border-r min-w-61.25 flex items-center justify-between">
        <Badge
          className="text-xxs! px-1.5 py-0.5"
          variant={COLORS.STATUS[renewal.status]}
        >
          {t(`renewal.${renewal.status}`)}
        </Badge>
        {['STANDBY', 'STANDBY_INSURER', 'COMPLETED'].includes(
          renewal.status,
        ) ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Scale className="m-1 stroke-warm-gray-200 stroke-w-[1.5px] w-4 h-4" />
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('renewal.offerNotReceived')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            className="w-6 h-6 hover:bg-secondary"
            onClick={(e) => {
              e.stopPropagation();
              onRowClick('comparison');
            }}
          >
            <Scale className="stroke-primary stroke-w-[1.5px]" />
          </Button>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap border-r min-w-42">
        {renewal.assignedToName == 'UNKNOWN_USER'
          ? t('renewal.UNKNOWN_USER')
          : renewal.assignedToName || '-'}
      </TableCell>
      {/* Identification de la police */}
      <TableCell className="whitespace-nowrap w-42 truncate border-x">
        {renewal.enterpriseName}
      </TableCell>
      <TableCell className="whitespace-nowrap border-r min-w-30">
        {renewal.policyNumber}
      </TableCell>
      <TableCell className="whitespace-nowrap border-r w-15 truncate">
        <Badge className="bg-white border border-gray text-black text-xxs px-1.5 py-0.5">
          {renewal.type}
        </Badge>
      </TableCell>
      <TableCell className="whitespace-nowrap border-r min-w-32.5">
        {renewal.insurerName}
      </TableCell>
      {/* Renouvellement */}
      <TableCell className="whitespace-nowrap border-x min-w-25">
        {renewal.currentPremium
          ? formatCurrency(renewal.currentPremium.toString())
          : '-'}
      </TableCell>
      <TableCell className="whitespace-nowrap border-r min-w-25">
        {renewal.renewalPremium
          ? formatCurrency(renewal.renewalPremium.toString())
          : '-'}
      </TableCell>
      <TableCell
        className={`whitespace-nowrap border-r font-medium min-w-17.5 text-center`}
      >
        {renewal.renewalPremium && renewal.currentPremium ? (
          <VariationBadge value={renewal.premiumVariation} />
        ) : (
          '-'
        )}
      </TableCell>
      {/* Contexte du contrat */}
      <TableCell className="whitespace-nowrap border-x min-w-25">
        {renderAmount(renewal.amendmentsTotal)}
      </TableCell>
      <TableCell className="whitespace-nowrap border-r min-w-25">
        {renderAmount(renewal.claimsTotal * -1)}
      </TableCell>
      {/* Rémunération et frais */}
      <TableCell className="whitespace-nowrap border-x">
        {renewal.commissionPercentage !== null
          ? `${renewal.commissionPercentage.toFixed(1)}%`
          : '-'}
      </TableCell>
      <TableCell className="whitespace-nowrap border-r">
        {renewal.honoraryFee
          ? formatCurrency(renewal.honoraryFee.toString())
          : '-'}
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {renewal.insurerFee
          ? formatCurrency(renewal.insurerFee.toString())
          : '-'}
      </TableCell>
    </TableRow>
  );
};
